/*
 * filterUtils.tsx
 * This contains the utility functions for the filter field
 * logic of the list component.
 */

import { fetchData } from "services/api/fetchData";
import { ListFilterFieldType, QueryParamType } from "types/ListTypes";


// Remove filter params from query params
export const removeFilterParams = (
    params: QueryParamType,
    filterFields?: ListFilterFieldType[],
) => {
    // Copy the current params
    const cleanedParams = { ...params };

    // Loop through the filter fields to delete the params
    filterFields?.forEach(filterField => {

        // Get the query param
        const queryParam = filterField.queryParam || filterField.name;

        // Delete the query param
        delete cleanedParams[queryParam];
        delete cleanedParams[`${queryParam}__gte`];
        delete cleanedParams[`${queryParam}__lte`];
    });

    return cleanedParams;
};


// Build query params from the active filters
export const buildFilterQueryParams = ({
    activeFilters,
    filterFields,
    queryParams,
    defaultParams,
    paramsToClear,
}: {
    activeFilters: Record<string, any>;
    filterFields?: ListFilterFieldType[];
    queryParams: QueryParamType;
    defaultParams?: QueryParamType;
    paramsToClear?: string[];
}) => {

    // Remove the filter field params from the current params
    const cleanedParams = removeFilterParams(queryParams, filterFields);

    // Also remove the params that switch along with the inactive items toggle
    paramsToClear?.forEach(param => {
        delete cleanedParams[param];
    });

    // Generate the new params
    let newParams: QueryParamType = {
        ...defaultParams,
        ...cleanedParams,
        page: 1,
    };

    // Loop through the filter fields
    filterFields?.forEach(filterField => {

        // Get the value and query param of the field
        const value = activeFilters[filterField.name];
        const queryParam = filterField.queryParam || filterField.name;

        // Delete the query param from the new params
        delete newParams[queryParam];
        delete newParams[`${queryParam}__gte`];
        delete newParams[`${queryParam}__lte`];

        // Skip empty values from the filter
        if (value === undefined || value === null || value === '' || value === 'none' || (Array.isArray(value) && value.length === 0)) {
            return;
        }

        // Handle daterange values filter
        if (filterField.type === 'daterange' && Array.isArray(value)) {
            if (value[0]) {
                newParams[`${queryParam}__gte`] = value[0];
            }

            if (value[1]) {
                newParams[`${queryParam}__lte`] = value[1];
            }

            return;
        }

        // Handle multiselect values
        if (Array.isArray(value)) {
            newParams[queryParam] = value.map(item => {
                if (typeof item === 'object' && item?.id) {
                    return item.id;
                }

                return item;
            }).join(',');

            return;
        }

        // Handle object values
        if (typeof value === 'object' && value?.id) {
            newParams[queryParam] = value.id;
            return;
        }

        // Handle primitive values
        newParams[queryParam] = value;
    });

    return newParams;
};


// Build the active filters from query params
export const buildActiveFiltersFromQueryParams = ({
    filterFields,
    queryParams,
}: {
    filterFields?: ListFilterFieldType[];
    queryParams: QueryParamType;
}) => {
    // Initialize variable to store the active filters
    const newActiveFilters: Record<string, any> = {};

    // Loop through the filter fields
    filterFields?.forEach(filterField => {

        // Get the query param of the field
        const queryParam = filterField.queryParam || filterField.name;

        // Handle daterange fields
        if (filterField.type === 'daterange') {
            const fromValue = queryParams[`${queryParam}__gte`];
            const toValue = queryParams[`${queryParam}__lte`];

            if (fromValue || toValue) {
                newActiveFilters[filterField.name] = [
                    fromValue ?? null,
                    toValue ?? null,
                ];
            }

            return;
        }

        // Get the single value
        const value = queryParams[queryParam];

        // Skip empty values
        if (value === undefined || value === null || value === '') {
            return;
        }

        // Handle multiselect values
        if (filterField.type === 'multiselect' && typeof value === 'string') {
            newActiveFilters[filterField.name] = value.split(',');
            return;
        }

        // Handle primitive values
        newActiveFilters[filterField.name] = value;
    });

    return newActiveFilters;
};


// Fetch dropdown data for the filter fields
export const fetchFilterDropdownData = async ({
    filterFields,
    activeFeatures,
    getCache,
    setCache,
    handleLogout,
    setFilterDropdownData,
    setFiltersLoaded,
}: {
    filterFields?: ListFilterFieldType[];
    activeFeatures: string[];
    getCache: (key: string) => any;
    setCache: (key: string, value: any) => void;
    handleLogout: (options?: { logoutFromServer?: boolean; showSessionAlert?: boolean }) => Promise<void>;
    setFilterDropdownData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
    setFiltersLoaded: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
    // Return if no filter fields are set
    if (!filterFields?.length) {
        setFiltersLoaded(true);
        return;
    }

    try {
        // Initialize variable to store the fetched dropdown data
        const newDropdownData: Record<string, any> = {};

        // Loop through the filter fields
        for (const filterField of filterFields) {

            // Only execute if an api request config is set
            if (filterField.apiRequest) {
                
                // Skip inactive feature fields
                if (filterField.requiredFeature && !activeFeatures.includes(filterField.requiredFeature)) {
                    continue;
                }

                // Get the requests for the field
                const requests = Array.isArray(filterField.apiRequest) ? filterField.apiRequest : [filterField.apiRequest];

                // Loop through the request config
                for (const request of requests) {

                    // Unpack the request config props
                    const { endpoint, params, object, useCache = false } = request;

                    // Generate a cache key
                    const cacheKey = `${endpoint}_${JSON.stringify(params ?? {})}`;

                    // Get the current cached response
                    if (useCache) {
                        const cachedData = getCache(cacheKey);
                        if (cachedData) {
                            newDropdownData[object] = cachedData;
                            continue;
                        }
                    }

                    // Otherwise fetch the data from the server
                    const fetchedData = await fetchData({ apiUrl: endpoint, params, handleLogout });

                    // Update the dropdown data state with the response
                    newDropdownData[object] = fetchedData.results;

                    // Store the data in the cache
                    if (useCache) {
                        setCache(cacheKey, fetchedData.results);
                    }
                }
            }
        }

        // Update the state
        setFilterDropdownData(newDropdownData);

    } catch (error) {
        console.error(error);
    } finally {
        setFiltersLoaded(true);
    }
};